import { IncorporationRequestDataType } from '@commenda/types';
import { USStatesToBeRemoved } from '@commenda/kakashi/src/components/AddressInput/AddressInput';
import {
  DirectorBase,
  TEquityInfo,
} from '@commenda/types/src/incorporation/types';
import {
  BoardPositionEnum,
  CorporationHasDirectorAdditionalInformation,
} from '@commenda/types/src/random';
import {
  FileWithType,
  WorkRequestResponseType,
} from '@commenda/types/src/work-request/types';
import {
  CorporationType,
  Country,
  IncorporationRequestStatus,
  LegacyDocumentType,
} from '@prisma/client';
import { State } from 'country-state-city';
import { TUAEIncorporationRequestData } from './../components/ae/types';

export type CorporationForEmailValidation = {
  id: number;
  contactEmail?: string;
  name?: string;
};

export const validateEquityInfo = (
  equityInfo: Partial<TEquityInfo>,
  directors: DirectorBase[],
  directorVsSharePercentage: Record<
    number,
    { name: string; sharePercentage: number; isDirector?: boolean }
  >,
  options?: { disableCheckForShareRetained?: boolean },
  newForm?: boolean
) => {
  if (!equityInfo.shareCount) {
    return {
      message: 'Please add no. of shares.',
    };
  }

  if (!equityInfo.price) {
    return {
      message: 'Please add per share price.',
    };
  }

  const companyValuation = equityInfo.price * equityInfo.shareCount;
  if (companyValuation < 1) {
    return {
      message: 'Company valuation is not valid.',
    };
  }

  let totalPercentage = 0;

  for (let i = 0; i < directors.length; i += 1) {
    const { sharePercentage } = directorVsSharePercentage[directors[i].id];

    if (!sharePercentage && sharePercentage !== 0) {
      return {
        message: `Please fill share percentage for director ${
          directorVsSharePercentage[directors[i].id].name
        }`,
      };
    }

    totalPercentage += sharePercentage;
  }

  // For the new form we are checking it through the directorVSPercentage
  if (newForm) {
    totalPercentage = 0;
    const allDirectorVsPercentage = Object.values(
      directorVsSharePercentage || {}
    );

    for (let idx = 0; idx < allDirectorVsPercentage.length; idx += 1) {
      const dsp = allDirectorVsPercentage[idx];
      if (!dsp.sharePercentage && dsp.sharePercentage !== 0) {
        return {
          message: `Please fill share percentage for the shareholder ${dsp.name}`,
        };
      }
      totalPercentage += dsp.sharePercentage;
    }
  }

  if (totalPercentage < 100) {
    return {
      message: 'Equity split should be equal to 100',
    };
  }

  if (totalPercentage > 100) {
    return {
      message: 'Total Equity Split cannot exceed 100',
    };
  }

  if (
    !options?.disableCheckForShareRetained &&
    !equityInfo.shareRetained &&
    equityInfo.shareRetained !== 0
  ) {
    return {
      message: 'Please add equity pool percentage',
    };
  }

  return null;
};

const validateEquityPercentage = (
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType }
) => {
  const allDirectors = data.directors;
  let totalPercentage = 0;
  for (let i = 0; i < allDirectors.length; i += 1) {
    const corporationDirector = allDirectors[i].corporationHasDirector.find(
      (chd) =>
        chd.corporationId === data?.corporationId &&
        chd.directorId === allDirectors[i].id
    );
    if (corporationDirector) {
      totalPercentage +=
        corporationDirector.additionalDirectorInformation?.sharePercentage || 0;
    }
  }

  if (totalPercentage !== 100) {
    return false;
  }
  return true;
};

const validateRolesForCCorp = (
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType }
) => {
  const allDirectors = data.directors;
  let boardPositions = [];

  for (let i = 0; i < allDirectors.length; i += 1) {
    const corporationDirector = allDirectors[i].corporationHasDirector.find(
      (chd) =>
        chd.corporationId === data?.corporationId &&
        chd.directorId === allDirectors[i].id
    );
    if (corporationDirector) {
      boardPositions = [
        ...boardPositions,
        ...(corporationDirector.additionalDirectorInformation?.boardPosition ||
          []),
      ];
    }
  }

  return (
    boardPositions.includes(BoardPositionEnum.President) &&
    boardPositions.includes(BoardPositionEnum.Secretary) &&
    boardPositions.includes(BoardPositionEnum.Treasurer)
  );
};

export const getPagesFilledForCCorpForm = (
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType }
): number => {
  if (!data) return null;

  const hasResponsibleParty = data?.directors?.some((dir) =>
    dir.corporationHasDirector?.find(
      (dirWithCorp) =>
        dirWithCorp.corporationId === data.corporationId &&
        dirWithCorp.directorId === dir.id &&
        dirWithCorp.additionalDirectorInformation?.isResponsibleParty
    )
  );

  const corporationDirectors = data?.directors?.filter((dir) =>
    dir.corporationHasDirector?.find(
      (dirWithCorp) =>
        dirWithCorp.corporationId === data.corporationId &&
        dirWithCorp.directorId === dir.id
    )
  );

  if (data && data.status === IncorporationRequestStatus.Completed) {
    return 6;
  }

  if (
    data &&
    data.status !== IncorporationRequestStatus.NotStarted &&
    data.status !== IncorporationRequestStatus.InProgress
  )
    return 5;

  if (
    data?.requestData?.shareCount &&
    data?.requestData?.sharePrice &&
    validateEquityPercentage(data)
  )
    return 4;

  if (hasResponsibleParty) return 3;

  if (
    validateRolesForCCorp(data) &&
    data?.requestData?.corporationType === CorporationType.CCorp
  )
    return 2;

  if (corporationDirectors?.length) return 1;

  if (
    data?.requestData?.companyLegalName &&
    data?.requestData?.companyNameSuffix &&
    data?.requestData?.businessDescription &&
    data?.requestData?.socialLink &&
    data?.requestData?.fiscalMonth
  )
    return 0;

  return null;
};

export const getPagesFilledForIndPvtLtdForm = (
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType }
): number => {
  if (!data) return null;

  const corporationDirectors = data?.directors?.filter((dir) =>
    dir.corporationHasDirector?.find(
      (dirWithCorp) =>
        dirWithCorp.corporationId === data.corporationId &&
        dirWithCorp.directorId === dir.id
    )
  );

  if (
    data &&
    data.status !== IncorporationRequestStatus.NotStarted &&
    data.status !== IncorporationRequestStatus.InProgress
  )
    return 3;

  if (
    data?.requestData?.shareCount &&
    data?.requestData?.sharePrice &&
    validateEquityPercentage(data)
  )
    return 2;

  // Check if directors have valid emails before allowing progression to Equity
  if (corporationDirectors?.length) {
    const companyEmail = data?.corporation?.contactEmail;
    const directorEmails = corporationDirectors.map(dir => dir.email?.trim().toLowerCase()).filter(Boolean);
    
    // Check for duplicate emails
    const allEmails = companyEmail ? [companyEmail.trim().toLowerCase(), ...directorEmails] : directorEmails;
    const duplicateEmails = allEmails.filter((email, index) => allEmails.indexOf(email) !== index);
    
    if (duplicateEmails.length > 0) {
      return 0; // Force staying on Directors step by returning the previous step index
    }
    
    return 1; // Directors step completed with valid emails, allow progression
  }

  if (
    data?.requestData?.companyLegalName &&
    data?.requestData?.businessDescription &&
    data?.requestData?.industry
  )
    return 0;

  return null;
};

export const getFileWithType = (
  files: FileWithType[],
  documentType: LegacyDocumentType
) => {
  return files?.find((file) => file.type === documentType)?.value?.at(-1);
};

export const filterShareholders = (
  directorWithCorporation: {
    corporationId: number;
    directorId: number;
  },
  directorId: number,
  corporationId: number,
  shareholderDirectorIds: Set<number>
) =>
  directorWithCorporation.corporationId === corporationId &&
  directorWithCorporation.directorId === directorId &&
  shareholderDirectorIds.has(directorId);

// Older rows still carry these keys; the type no longer declares them.
export const omitLegacyOwnershipFlags = (
  additionalDirectorInformation: CorporationHasDirectorAdditionalInformation & {
    isShareholder?: boolean;
    isBeneficialOwner?: boolean;
  } = {}
): CorporationHasDirectorAdditionalInformation => {
  const withoutLegacyFlags = { ...additionalDirectorInformation };
  delete withoutLegacyFlags.isShareholder;
  delete withoutLegacyFlags.isBeneficialOwner;
  return withoutLegacyFlags;
};

export const getStatesDropdown = (country: Country) => {
  if (!country) return [];
  const states = State.getStatesOfCountry(country)
    .filter((state) => {
      if (country === Country.US) {
        return !USStatesToBeRemoved.includes(state.isoCode); // For US we have a lot of values coming from the third party which are not states, so filter them out.
      }
      return true;
    })
    .map((state) => ({
      text: state.name,
      value: state.isoCode,
    }));
  return states;
};

export const getLastCurrentStepForUAEIncorporation = (
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  }
) => {
  // Checking for Key positions
  const manager = workRequest.directors.find((eachDirector) =>
    eachDirector.corporationHasDirector.find(
      (eachCorpWithDirector) =>
        eachCorpWithDirector.corporationId === workRequest.corporationId &&
        eachCorpWithDirector.additionalDirectorInformation?.boardPosition?.includes(
          BoardPositionEnum.Manager
        )
    )
  );

  const secretary = workRequest.directors.find((eachDirector) =>
    eachDirector.corporationHasDirector.find(
      (eachCorpWithDirector) =>
        eachCorpWithDirector.corporationId === workRequest.corporationId &&
        eachCorpWithDirector.additionalDirectorInformation?.boardPosition?.includes(
          BoardPositionEnum.Secretary
        )
    )
  );

  const bankSignatory = workRequest.directors.find((eachDirector) =>
    eachDirector.corporationHasDirector.find(
      (eachCorpWithDirector) =>
        eachCorpWithDirector.corporationId === workRequest.corporationId &&
        eachCorpWithDirector.additionalDirectorInformation?.boardPosition?.includes(
          BoardPositionEnum.BankSignatory
        )
    )
  );

  const director = workRequest.directors.find((eachDirector) =>
    eachDirector.corporationHasDirector.find(
      (eachCorpWithDirector) =>
        eachCorpWithDirector.corporationId === workRequest.corporationId &&
        eachCorpWithDirector.additionalDirectorInformation?.isDirector
    )
  );

  if (manager?.id && secretary?.id && bankSignatory?.id && director?.id)
    return 2;

  if (
    workRequest?.requestData?.companyLegalName &&
    workRequest?.requestData?.companyLegalName2 &&
    workRequest?.requestData?.companyLegalName3
  )
    return 1;

  if (
    workRequest.requestData?.businessDescription &&
    workRequest.requestData?.businessActivity?.length > 0 &&
    workRequest.requestData?.estimatedAnnualTurnover &&
    workRequest.requestData?.exportCountries?.length > 0 &&
    workRequest.requestData?.importCountries?.length > 0
  )
    return 0;

  return null;
};

// Helper function for email validation in Indian incorporation forms (LLP and PVT)
export const checkEmailValidityForDirector = (
  director: DirectorBase,
  stakeholderName: string,
  stakeholderId: string,
  companyEmailLower: string | undefined,
  existingDirectorEmails: string[],
  existingDirectors: DirectorBase[],
  emailConflicts: any[],
  shareholderWithError: any[],
  getDirectorNameFn: (director: DirectorBase) => string
): boolean => {
  const directorEmail = director?.email?.trim().toLowerCase();
  
  if (!directorEmail) return true; // No email to validate
  
  // Check if director email matches company email
  if (companyEmailLower && directorEmail === companyEmailLower) {
    emailConflicts.push({
      stakeholderName,
      stakeholderEmail: director.email,
      conflictType: 'company',
      conflictTarget: 'company email'
    });
    shareholderWithError.push({
      name: stakeholderName,
      id: stakeholderId,
    });
    return false;
  }
  
  // Check if this director's email appears multiple times in director list
  const directorEmailCount = existingDirectorEmails.filter(email => email === directorEmail).length;
  if (directorEmailCount > 1) {
    const conflictingDirector = existingDirectors.find(d => 
      d.email?.trim().toLowerCase() === directorEmail && d.id !== director.id
    );
    emailConflicts.push({
      stakeholderName,
      stakeholderEmail: director.email,
      conflictType: 'director',
      conflictTarget: conflictingDirector ? getDirectorNameFn(conflictingDirector) : 'another director'
    });
    shareholderWithError.push({
      name: stakeholderName,
      id: stakeholderId,
    });
    return false;
  }
  
  return true; // Email is valid
};

// Helper function for cross-entity email validation in Indian incorporation forms (LLP and PVT)
export const checkCompanyEmailUniqueness = (
  proposedCompanyEmail: string,
  currentCorporationId: number,
  allCorporations: CorporationForEmailValidation[]
): { isUnique: boolean; conflictingCorporation?: CorporationForEmailValidation } => {
  const proposedEmailLower = proposedCompanyEmail?.trim().toLowerCase();
  
  if (!proposedEmailLower) {
    return { isUnique: true };
  }
  
  // Find any other corporation with the same email (excluding current one)
  const conflictingCorporation = allCorporations.find(corp => 
    corp.id !== currentCorporationId && 
    corp.contactEmail?.trim().toLowerCase() === proposedEmailLower
  );
  
  return {
    isUnique: !conflictingCorporation,
    conflictingCorporation
  };
};

// Helper function to check if an individual's email conflicts with any company email across all entities
export const checkIndividualEmailAgainstCompanyEmails = (
  proposedIndividualEmail: string,
  allCorporations: CorporationForEmailValidation[]
): { isUnique: boolean; conflictingCorporation?: CorporationForEmailValidation } => {
  const proposedEmailLower = proposedIndividualEmail?.trim().toLowerCase();
  
  if (!proposedEmailLower) {
    return { isUnique: true };
  }
  
  // Find any corporation with the same email as the proposed individual email
  const conflictingCorporation = allCorporations.find(corp => 
    corp.contactEmail?.trim().toLowerCase() === proposedEmailLower
  );
  
  return {
    isUnique: !conflictingCorporation,
    conflictingCorporation
  };
};
